import {
  collectionFilterPreferenceSchema,
  collectionFilterPreferencesSchema,
  collectionFilterScopeKey,
  collectionFilterScopeSchema,
  normalizeCollectionFilterConditions,
  type CollectionFilterConditions,
  type CollectionFilterPreference,
  type CollectionFilterPreferenceInput,
  type CollectionFilterPreferences,
  type CollectionFilterScope,
} from '@kiipu/app-protocol';

type StoragePort = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;
type Pending = CollectionFilterPreferenceInput & { mutationId: string; changedAt: number };
type Entry = {
  saved: CollectionFilterPreference;
  desired?: Pending;
  sent?: Pending;
  running?: Promise<void>;
  timer?: ReturnType<typeof setTimeout>;
  error?: unknown;
  conflict?: CollectionFilterPreference;
  acknowledgedRevision?: number;
  deleted?: boolean;
};
export type CollectionFilterSaveStatus = {
  pending: boolean;
  offline: boolean;
  error?: unknown;
  conflict?: CollectionFilterPreference;
  storageError?: unknown;
  deleted?: boolean;
};
export type CollectionFilterStoreOptions = {
  owner: string;
  storage?: StoragePort;
  online: () => boolean;
  load: (signal: AbortSignal) => Promise<CollectionFilterPreferences>;
  save: (
    input: CollectionFilterPreferenceInput,
    signal: AbortSignal,
  ) => Promise<CollectionFilterPreference>;
};
const empty: CollectionFilterConditions = {};
const epoch = '1970-01-01T00:00:00.000Z';
const same = (left: CollectionFilterConditions, right: CollectionFilterConditions) =>
  JSON.stringify(left) === JSON.stringify(right);
const statusCode = (error: unknown) =>
  error && typeof error === 'object' && 'status' in error ? error.status : undefined;
function virtualPreference(scope: CollectionFilterScope): CollectionFilterPreference {
  return { scope, conditions: empty, revision: 0, updatedAt: epoch };
}
function storedJSON(raw: string | null | undefined): unknown {
  try {
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}
function pendingValue(input: unknown): Pending | undefined {
  if (!input || typeof input !== 'object') return;
  const value = input as Record<string, unknown>;
  const scope = collectionFilterScopeSchema.safeParse(value.scope);
  if (
    !scope.success ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 0 ||
    typeof value.mutationId !== 'string' ||
    value.mutationId.length > 100 ||
    !Number.isFinite(value.changedAt)
  )
    return;
  try {
    return {
      scope: scope.data,
      conditions: normalizeCollectionFilterConditions(value.conditions),
      revision: Number(value.revision),
      mutationId: value.mutationId,
      changedAt: Number(value.changedAt),
    };
  } catch {
    return;
  }
}
function newer(left: Pending, right: Pending) {
  return (
    left.changedAt > right.changedAt ||
    (left.changedAt === right.changedAt && left.mutationId > right.mutationId)
  );
}

/** The store outlives page mounts; each account/server/scope has its own durable intent. */
export class CollectionFilterStore {
  readonly storagePrefix: string;
  private readonly snapshotKey: string;
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private controller = new AbortController();
  private serial = 0;
  private version = -1;
  private stamp = 0;
  private writes = 0;
  private disposed = false;
  private loading?: Promise<void>;
  ready = false;
  error?: unknown;
  storageError?: unknown;

  constructor(private readonly options: CollectionFilterStoreOptions) {
    this.storagePrefix = `kiipu.collection-filters.v1:${encodeURIComponent(options.owner)}:`;
    this.snapshotKey = this.storagePrefix + 'snapshot';
    this.readSnapshot();
    this.readPending();
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = () => this.serial;
  private emit() {
    if (this.disposed) return;
    this.serial++;
    this.listeners.forEach((listener) => listener());
  }
  private pendingKey(scope: CollectionFilterScope) {
    return this.storagePrefix + 'pending:' + encodeURIComponent(collectionFilterScopeKey(scope));
  }
  private entry(scope: CollectionFilterScope) {
    const key = collectionFilterScopeKey(scope);
    let value = this.entries.get(key);
    if (!value) {
      value = { saved: virtualPreference(scope) };
      this.entries.set(key, value);
    }
    return value;
  }
  preference = (scope: CollectionFilterScope): CollectionFilterPreference => {
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    return entry?.saved ?? virtualPreference(scope);
  };
  current = (scope: CollectionFilterScope): CollectionFilterConditions => {
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    return (
      entry?.desired?.conditions ?? entry?.sent?.conditions ?? entry?.saved.conditions ?? empty
    );
  };
  status = (scope: CollectionFilterScope): CollectionFilterSaveStatus => {
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    const pending = Boolean(entry?.desired || entry?.running);
    return {
      pending,
      offline: pending && !this.options.online(),
      ...(entry?.error ? { error: entry.error } : {}),
      ...(entry?.conflict ? { conflict: entry.conflict } : {}),
      ...(pending && this.storageError ? { storageError: this.storageError } : {}),
      ...(entry?.deleted ? { deleted: true } : {}),
    };
  };

  private readSnapshot() {
    try {
      const raw = this.options.storage?.getItem(this.snapshotKey);
      if (!raw) return;
      const value = collectionFilterPreferencesSchema.safeParse(storedJSON(raw));
      if (!value.success || value.data.version < this.version) return;
      this.version = value.data.version;
      for (const pref of value.data.items) {
        const entry = this.entry(pref.scope);
        if (pref.revision >= entry.saved.revision) entry.saved = pref;
      }
      this.ready = true;
    } catch {
      // A malformed optional snapshot never becomes an automatic empty write.
    }
  }
  private readPending() {
    const storage = this.options.storage;
    if (!storage) return;
    try {
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (!key?.startsWith(this.storagePrefix + 'pending:')) continue;
        const raw = storage.getItem(key);
        const pending = pendingValue(storedJSON(raw));
        if (!pending || key !== this.pendingKey(pending.scope)) continue;
        const entry = this.entry(pending.scope);
        if (entry.deleted) continue;
        const current = entry.desired ?? entry.sent;
        if (!current || newer(pending, current)) {
          entry.desired = pending;
          entry.error = undefined;
          entry.conflict = undefined;
        }
        this.stamp = Math.max(this.stamp, pending.changedAt);
      }
    } catch {
      // Memory remains usable when browser storage is unavailable.
    }
  }
  private persistPending(entry: Entry) {
    const storage = this.options.storage;
    if (!storage) {
      if (entry.desired || entry.sent)
        this.storageError = new Error('本机存储不可用，筛选更改暂存在此窗口。');
      return;
    }
    const pending = entry.desired ?? entry.sent;
    const key = this.pendingKey(entry.saved.scope);
    try {
      const raw = storage.getItem(key);
      const stored = pendingValue(storedJSON(raw));
      // Another window's later click owns the durable key until we ingest it.
      if (stored && pending && newer(stored, pending)) return;
      if (pending) storage.setItem(key, JSON.stringify(pending));
      else if (!stored || same(stored.conditions, entry.saved.conditions)) storage.removeItem(key);
      this.storageError = undefined;
    } catch (error) {
      this.storageError = error;
    }
  }
  private persistSnapshot(authoritative = false) {
    const storage = this.options.storage;
    if (!storage || this.version < 0) return;
    try {
      const merged = new Map<string, CollectionFilterPreference>();
      if (!authoritative) {
        const raw = storage.getItem(this.snapshotKey);
        const old = collectionFilterPreferencesSchema.safeParse(storedJSON(raw));
        if (old.success) {
          if (old.data.version > this.version) return;
          for (const pref of old.data.items) merged.set(collectionFilterScopeKey(pref.scope), pref);
        }
      }
      for (const { saved, deleted } of this.entries.values()) {
        const key = collectionFilterScopeKey(saved.scope);
        if (!saved.revision || deleted) {
          merged.delete(key);
          continue;
        }
        if ((merged.get(key)?.revision ?? -1) <= saved.revision) merged.set(key, saved);
      }
      storage.setItem(
        this.snapshotKey,
        JSON.stringify({ items: [...merged.values()], version: this.version }),
      );
      this.storageError = undefined;
    } catch (error) {
      this.storageError = error;
    }
  }
  private schedule(entry: Entry) {
    clearTimeout(entry.timer);
    if (!this.disposed && !entry.error && !entry.conflict && this.options.online()) {
      entry.timer = setTimeout(() => void this.flush(entry.saved.scope), 300);
    }
  }
  stage = (
    scope: CollectionFilterScope,
    conditions: CollectionFilterConditions,
    baseRevision?: number,
  ) => {
    if (this.disposed) return;
    const entry = this.entry(scope);
    if (entry.deleted) return;
    this.stamp = Math.max(Date.now(), this.stamp + 1);
    entry.desired = {
      scope: entry.saved.scope,
      conditions: normalizeCollectionFilterConditions(conditions),
      // An open editor keeps the revision of the conditions the user actually saw.
      // Only this store's own acknowledged writes may advance that frozen baseline.
      revision: Math.max(baseRevision ?? entry.saved.revision, entry.acknowledgedRevision ?? 0),
      changedAt: this.stamp,
      mutationId: crypto.randomUUID(),
    };
    this.persistPending(entry);
    this.schedule(entry);
    this.emit();
  };
  refresh = (): Promise<void> => {
    if (this.disposed) return Promise.resolve();
    if (this.loading) return this.loading;
    if (!this.options.online()) {
      this.ready = true;
      this.emit();
      return Promise.resolve();
    }
    const controller = new AbortController();
    const writesAtStart = this.writes;
    const abort = () => controller.abort();
    this.controller.signal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 3000);
    this.loading = this.options
      .load(controller.signal)
      .then((input) => {
        if (this.disposed || controller.signal.aborted) return;
        const data = collectionFilterPreferencesSchema.parse(input);
        if (data.version < this.version) return;
        this.version = data.version;
        const present = new Set(data.items.map((pref) => collectionFilterScopeKey(pref.scope)));
        for (const [key, entry] of this.entries) {
          if (
            writesAtStart === this.writes &&
            !present.has(key) &&
            !entry.desired &&
            !entry.running
          )
            this.entries.delete(key);
        }
        for (const pref of data.items) {
          const entry = this.entry(pref.scope);
          if (pref.revision >= entry.saved.revision) entry.saved = pref;
          if (entry.conflict) entry.conflict = pref;
        }
        this.error = undefined;
        this.persistSnapshot(writesAtStart === this.writes);
      })
      .catch((error: unknown) => {
        if (!this.disposed) this.error = error;
      })
      .finally(() => {
        clearTimeout(timeout);
        this.controller.signal.removeEventListener('abort', abort);
        this.loading = undefined;
        if (this.disposed) return;
        this.ready = true;
        this.emit();
      });
    return this.loading;
  };
  flush = async (scope: CollectionFilterScope): Promise<void> => {
    if (this.disposed || !this.options.online()) return;
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    if (!entry) return;
    clearTimeout(entry.timer);
    if (entry.running) return entry.running;
    if (!entry.desired || entry.error || entry.conflict) return;
    const sent = entry.desired;
    entry.desired = undefined;
    entry.sent = sent;
    entry.running = this.options
      .save(
        { scope: sent.scope, conditions: sent.conditions, revision: sent.revision },
        this.controller.signal,
      )
      .then((input) => {
        if (this.disposed) return;
        const saved = collectionFilterPreferenceSchema.parse(input);
        if (collectionFilterScopeKey(saved.scope) !== collectionFilterScopeKey(scope))
          throw new Error('筛选保存响应范围不一致。');
        if (saved.revision >= entry.saved.revision) entry.saved = saved;
        entry.acknowledgedRevision = Math.max(entry.acknowledgedRevision ?? 0, saved.revision);
        // A successful PUT is a usable local snapshot even when the first GET failed.
        // Zero is a conservative collection-version floor; the next GET supplies its real version.
        this.version = Math.max(0, this.version);
        this.writes++;
        // Acknowledging our own preceding write safely rebases its newer local successor.
        if (entry.desired) entry.desired.revision = saved.revision;
        entry.error = undefined;
        entry.conflict = undefined;
        this.persistSnapshot();
      })
      .catch(async (error: unknown) => {
        if (this.disposed) return;
        entry.desired ??= sent;
        entry.error = error;
        if (
          scope.kind === 'folder' &&
          statusCode(error) === 404 &&
          (error as { code?: string }).code === 'folder_not_found'
        ) {
          entry.deleted = true;
          entry.desired = undefined;
          entry.sent = undefined;
          try {
            this.options.storage?.removeItem(this.pendingKey(scope));
          } catch (storageError) {
            this.storageError = storageError;
          }
          this.persistSnapshot();
          return;
        }
        if (statusCode(error) === 409) {
          const details = (error as { details?: unknown }).details;
          const candidate =
            details && typeof details === 'object' && 'current' in details
              ? details.current
              : details;
          if (
            candidate === null &&
            details &&
            typeof details === 'object' &&
            'current' in details
          ) {
            entry.saved = virtualPreference(scope);
            entry.acknowledgedRevision = 0;
            entry.conflict = entry.saved;
            return;
          }
          const parsed = collectionFilterPreferenceSchema.safeParse(candidate);
          if (
            parsed.success &&
            collectionFilterScopeKey(parsed.data.scope) === collectionFilterScopeKey(scope)
          ) {
            entry.conflict = parsed.data;
            if (parsed.data.revision >= entry.saved.revision) entry.saved = parsed.data;
          } else {
            await this.refresh();
            if (!this.disposed && !this.error) entry.conflict = entry.saved;
          }
        }
      })
      .finally(() => {
        entry.running = undefined;
        entry.sent = undefined;
        if (this.disposed) return;
        this.persistPending(entry);
        this.emit();
        if (entry.desired && !entry.error && !entry.conflict) void this.flush(scope);
      });
    this.emit();
    return entry.running;
  };
  retry = async (scope: CollectionFilterScope) => {
    if (this.disposed) return;
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    if (!entry || entry.conflict) return;
    await this.refresh();
    entry.error = undefined;
    this.emit();
    await this.flush(scope);
  };
  adopt = (scope: CollectionFilterScope) => {
    if (this.disposed) return;
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    if (!entry?.conflict) return;
    entry.saved = entry.conflict;
    entry.desired = undefined;
    entry.error = undefined;
    entry.conflict = undefined;
    try {
      this.options.storage?.removeItem(this.pendingKey(scope));
    } catch (error) {
      this.storageError = error;
    }
    this.persistSnapshot();
    this.emit();
  };
  overwrite = async (scope: CollectionFilterScope) => {
    if (this.disposed) return;
    const entry = this.entries.get(collectionFilterScopeKey(scope));
    if (!entry?.desired) return;
    await this.refresh();
    if (this.disposed || !entry.desired) return;
    entry.desired.revision = entry.conflict?.revision ?? entry.saved.revision;
    entry.conflict = undefined;
    entry.error = undefined;
    this.persistPending(entry);
    this.emit();
    await this.flush(scope);
  };
  /** Called for storage events and before network recovery, never mirrors writes in a loop. */
  synchronize = () => {
    if (this.disposed) return;
    this.readSnapshot();
    this.readPending();
    this.emit();
  };
  resume = async () => {
    if (this.disposed) return;
    this.synchronize();
    await this.refresh();
    if (!this.options.online()) return;
    for (const entry of this.entries.values()) {
      if (entry.error && !entry.conflict && !entry.deleted) entry.error = undefined;
      if (entry.desired && !entry.conflict) void this.flush(entry.saved.scope);
    }
    this.emit();
  };
  drain = () => {
    if (this.disposed || !this.options.online()) return;
    for (const entry of this.entries.values()) {
      if (entry.desired && !entry.error && !entry.conflict) void this.flush(entry.saved.scope);
    }
  };
  dispose() {
    if (this.disposed) return;
    // Keep durable intent for the same account's next authenticated session.
    for (const entry of this.entries.values()) {
      clearTimeout(entry.timer);
      this.persistPending(entry);
    }
    this.disposed = true;
    this.controller.abort();
    this.listeners.clear();
  }
}
